import Link from "next/link";
import type { ExploreStoryCard } from "../types";

interface MobileStoryTextOnlyCardProps {
  story: ExploreStoryCard;
  exploreBase: string;
  /** Show divider below the card (default true) */
  showDivider?: boolean;
}

/**
 * Pure text card with left accent bar — Monocle/HN density style.
 * No image, maximum headline real-estate. Used for dense
 * "quick scan" sections in mixed-rhythm feeds.
 */
export function MobileStoryTextOnlyCard({
  story,
  exploreBase,
  showDivider = true,
}: MobileStoryTextOnlyCardProps) {
  const primaryTag = story.productTags[0] ?? story.topicTags[0];
  const categoryLabel = story.categoryDisplayName;

  return (
    <div>
      <Link
        className="block border-[var(--explore-accent-secondary)] border-l-[3px] pl-4 no-underline active:opacity-80"
        href={`${exploreBase}/stories/${story.storyId}`}
        style={{ fontFamily: "var(--homepage-sans-stack)" }}
      >
        {/* Category + new indicator */}
        <div className="mb-1 flex items-center gap-2">
          {categoryLabel && (
            <span className="font-bold text-[0.68rem] text-[var(--explore-accent-secondary)] uppercase tracking-[0.08em]">
              {story.categoryEmoji && (
                <span className="mr-0.5">{story.categoryEmoji}</span>
              )}
              {categoryLabel}
            </span>
          )}
          {story.isNew && (
            <span className="flex items-center gap-1">
              <span className="h-1.5 w-1.5 animate-[explore-pulse-dot_1.5s_ease-in-out_infinite] rounded-full bg-[var(--explore-new-indicator)]" />
              <span className="font-bold text-[0.6rem] text-[var(--explore-new-indicator)] uppercase tracking-[0.04em]">
                New
              </span>
            </span>
          )}
        </div>

        {/* Headline — full width, generous 3-line clamp */}
        <h3
          className="mb-1 line-clamp-3 text-pretty font-bold text-[1rem] text-[var(--explore-text-primary)] leading-[1.35]"
          style={{ fontFamily: "var(--explore-card-title-stack)" }}
        >
          {story.headline}
        </h3>

        {/* Tag + time byline */}
        <div className="flex items-center gap-1.5 text-[0.72rem]">
          {primaryTag && (
            <span className="font-semibold text-[var(--explore-text-primary)]">
              {primaryTag.displayName}
            </span>
          )}
          {primaryTag && (
            <span className="text-[var(--explore-text-muted)]">·</span>
          )}
          <span className="text-[var(--explore-text-muted)]">
            {story.timeAgo}
          </span>
        </div>
      </Link>

      {showDivider && (
        <div className="mt-3 border-[var(--explore-border-light)] border-b" />
      )}
    </div>
  );
}
